/**
 * Jurídico (ADR 0023, especificação V4): formulários editáveis e versionados. A mesma definição
 * valida o que a administração salva e o que a pessoa responde (na tela e no servidor).
 */
import { z } from 'zod';
import {
  costAllocationInput,
  costAllocationIssues,
  type CostAllocation,
} from './cost-allocation.ts';
import {
  defineFormSchemas,
  formFieldVisible,
  validateFormFieldValues,
  type FormFinancialProfile,
} from './forms.ts';
import {
  LEGAL_DRAFT_ORIGINS,
  LEGAL_OUTCOMES,
  LEGAL_REVIEW_STATUSES,
} from './legal-formalization.ts';
import { localizedText } from './schemas.ts';

/** Grupos de "Solicitações Jurídicas" (V4, seção 2). */
export const LEGAL_SERVICE_GROUPS = [
  'contracts',
  'terms',
  'nda',
  'amendments',
  'terminations',
  'opinions',
  'powers_of_attorney',
  'other',
] as const;
export type LegalServiceGroup = (typeof LEGAL_SERVICE_GROUPS)[number];

export const LEGAL_CONFIDENTIALITY = ['internal', 'restricted', 'confidential'] as const;
export const LEGAL_COMPARTMENTS = ['labor', 'personal_data', 'litigation', 'corporate'] as const;
/** Interno: só a equipe jurídica; compartilhado: também quem pediu e os participantes (ADR 0024). */
export const LEGAL_VISIBILITY = ['internal', 'shared'] as const;

/**
 * Tipos de campo desta etapa (V4, seção 4). Os de cadastro guardam o id do registro corporativo;
 * "attachment" indica um documento exigido, enviado como anexo da solicitação.
 */
export const LEGAL_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'money',
  'select',
  'multi_select',
  'checkbox',
  'user',
  'legal_entity',
  'department',
  'cost_center',
  'partner',
  'project',
  'attachment',
  'address',
  'contract_ref',
] as const;
export type LegalFieldType = (typeof LEGAL_FIELD_TYPES)[number];

/** Tipos que apontam para um cadastro corporativo (guardam o id). */
export const LEGAL_LOOKUP_TYPES: readonly LegalFieldType[] = [
  'user',
  'legal_entity',
  'department',
  'cost_center',
  'partner',
  'project',
];

// Mecanismo comum de formulários (forms.ts): o Jurídico usa os tipos acima, com as mesmas regras.
const legalForms = defineFormSchemas(LEGAL_FIELD_TYPES);

export const legalFieldInput = legalForms.field;
export type LegalField = z.infer<typeof legalFieldInput>;

export const legalFormSchemaInput = legalForms.schema;
export type LegalFormSchema = z.infer<typeof legalFormSchemaInput>;

/** O campo aparece com as respostas atuais? (condição sobre um campo anterior) */
export function legalFieldVisible(field: LegalField, values: Record<string, unknown>): boolean {
  return formFieldVisible(field, values);
}

/**
 * Parceiro da solicitação: o campo de parceiro do formulário, quando visível e preenchido, manda
 * (com ele a tela nem mostra o seletor geral); sem esse campo, vale o parceiro informado.
 */
export function legalRequestPartner(
  schema: LegalFormSchema,
  fieldValues: Record<string, unknown>,
  partnerId: string | null | undefined,
): string | null {
  const field = schema.fields.find(
    (f) => f.type === 'partner' && legalFieldVisible(f, fieldValues),
  );
  const chosen = field ? fieldValues[field.key] : null;
  return typeof chosen === 'string' && chosen !== '' ? chosen : (partnerId ?? null);
}

/**
 * Confere as respostas contra o formulário: obrigatórios visíveis, tipos, opções e campos
 * desconhecidos. Campos escondidos pela condição não podem ter resposta. Devolve os problemas
 * no formato { path, message } usado pelas telas.
 */
export function validateLegalFieldValues(
  schema: LegalFormSchema,
  values: Record<string, unknown>,
): { path: string; message: string }[] {
  return validateFormFieldValues(schema, values);
}

/** Dados da modalidade que a administração jurídica ajusta pela tela. */
export const legalServiceInput = z.object({
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
    .default({ 'pt-BR': '', en: '' }),
  defaultConfidentiality: z.enum(LEGAL_CONFIDENTIALITY),
  compartment: z.enum(LEGAL_COMPARTMENTS).nullable(),
  isActive: z.boolean(),
  isReady: z.boolean(),
  /** Motivo de liberar ou suspender o uso (obrigatório quando "pronta" muda; decisão de 27/10). */
  readyReason: z.string().trim().max(2000).nullish(),
  sortOrder: z.number().int().min(1).max(999),
});
export type LegalServiceInput = z.infer<typeof legalServiceInput>;

/** Existe valor? Definido, a definir (ainda desconhecido) ou sem valor financeiro. */
export const LEGAL_VALUE_STATUSES = ['defined', 'to_define', 'none'] as const;
export const LEGAL_VALUE_NATURES = [
  'fixed',
  'estimated',
  'per_delivery',
  'variable',
  'royalties',
  'other',
] as const;
/** O valor informado é o total do contrato, o valor de cada período ou o de cada entrega. */
export const LEGAL_VALUE_BASES = ['total', 'per_period', 'per_delivery'] as const;
export const LEGAL_PERIODICITIES = [
  'one_time',
  'monthly',
  'bimonthly',
  'quarterly',
  'semiannual',
  'yearly',
  'per_delivery',
  'not_applicable',
] as const;
const RECURRING = ['monthly', 'bimonthly', 'quarterly', 'semiannual', 'yearly'];

/** Vigência: prazo determinado, indeterminado ou condicionada a um evento. */
export const LEGAL_TERM_TYPES = ['fixed', 'indefinite', 'conditional'] as const;

export interface LegalTermFields {
  termType?: string | null;
  termStart?: string | null;
  termEnd?: string | null;
  termCondition?: string | null;
  autoRenewal?: boolean | null;
  noticeDays?: number | null;
}

/**
 * Vigência (modalidades contratuais): coerência no rascunho e obrigatórios no envio. Prazo
 * determinado tem início e término; indeterminado não tem término; condicionada descreve a
 * condição. Renovação automática só com prazo determinado. Nenhum prazo legal é calculado.
 */
/**
 * Aditivo (F19): a vigência é opcional e o término nunca é exigido, porque o aditivo pode não
 * ter data de encerramento própria (homologação de 21/10/2026; ADR 0058).
 */
export const LEGAL_TERM_OPTIONAL_SERVICES: readonly string[] = ['F19'];

export function legalTermIssues(
  financial: FormFinancialProfile,
  r: LegalTermFields,
  submit: boolean,
  serviceCode?: string,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  if (financial !== 'contract') return issues;
  const optional = !!serviceCode && LEGAL_TERM_OPTIONAL_SERVICES.includes(serviceCode);
  const add = (path: string, message: string) => issues.push({ path, message });
  if (!r.termType) {
    if (submit && !optional) add('termType', 'Obrigatório');
    return issues;
  }
  if (submit && !r.termStart) add('termStart', 'Informe o início');
  if (r.termType === 'fixed' && submit && !r.termEnd && !optional) {
    add('termEnd', 'Informe o término');
  }
  if (r.termType === 'indefinite' && r.termEnd)
    add('termEnd', 'Prazo indeterminado não tem término');
  if (r.termType === 'conditional' && submit && !r.termCondition) {
    add('termCondition', 'Descreva a condição');
  }
  if (r.termStart && r.termEnd && r.termEnd < r.termStart) {
    add('termEnd', 'O término não pode ser antes do início');
  }
  if (r.autoRenewal && r.termType !== 'fixed') {
    add('autoRenewal', 'Renovação automática só com prazo determinado');
  }
  return issues;
}

/**
 * Parcelas do formulário (ex.: F01) coerentes com o valor: pagamento único não tem mais de uma
 * parcela, e parcelas valem só para o valor total do contrato.
 */
export function legalInstallmentIssues(
  fieldValues: Record<string, unknown>,
  r: { periodicity?: string | null; valueBasis?: string | null },
): { path: string; message: string }[] {
  const n = fieldValues.parcelas;
  if (typeof n !== 'number') return [];
  const issues: { path: string; message: string }[] = [];
  if (n < 1 || !Number.isInteger(n))
    issues.push({ path: 'parcelas', message: 'Número inteiro de parcelas' });
  else if (n > 1 && r.periodicity === 'one_time') {
    issues.push({ path: 'parcelas', message: 'Pagamento único não tem mais de uma parcela' });
  } else if (n > 1 && r.valueBasis && r.valueBasis !== 'total') {
    issues.push({ path: 'parcelas', message: 'Parcelas valem para o valor total do contrato' });
  }
  return issues;
}

// ----- Solicitações -----

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Abrir o rascunho: modalidade, empresa e título (o resto vem nas etapas seguintes). */
export const legalRequestCreateInput = z.object({
  serviceId: z.uuid(),
  legalEntityId: z.uuid(),
  title: z.string().trim().min(1).max(150),
});

/** Campos base da solicitação (V4, seção 4) e as respostas dos campos específicos. */
export const legalRequestDraftInput = z
  .object({
    legalEntityId: z.uuid(),
    title: z.string().trim().min(1).max(150),
    description: optional(10000),
    justification: optional(5000),
    departmentId: optionalId,
    costCenterId: optionalId,
    projectId: optionalId,
    partnerId: optionalId,
    /** Gestor do contrato (colaborador cadastrado), nas modalidades contratuais. */
    businessOwnerId: optionalId,
    /** Centro de custo principal ou rateio, nas modalidades contratuais (ADR 0029). */
    allocation: costAllocationInput.nullable().optional(),
    /** Origem da minuta, nas modalidades contratuais (D-14). */
    draftOrigin: z.enum(LEGAL_DRAFT_ORIGINS).nullable().optional(),
    priority: z.enum(['normal', 'high', 'critical']).default('normal'),
    priorityReason: optional(1000),
    neededBy: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    amount: z
      .string()
      .trim()
      .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números, com ponto nos centavos')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    currency: z
      .string()
      .regex(/^([A-Z]{3})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    periodicity: z
      .enum(LEGAL_PERIODICITIES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    /** Estrutura do valor (ADR 0030): existência, natureza e base. */
    valueStatus: z
      .enum(LEGAL_VALUE_STATUSES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    valueNature: z
      .enum(LEGAL_VALUE_NATURES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    valueBasis: z
      .enum(LEGAL_VALUE_BASES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    /** Vigência estruturada (modalidades contratuais). */
    termType: z
      .enum(LEGAL_TERM_TYPES)
      .or(z.literal(''))
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    termStart: z
      .string()
      .regex(/^(\d{4}-\d{2}-\d{2})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    termEnd: z
      .string()
      .regex(/^(\d{4}-\d{2}-\d{2})?$/)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    termCondition: optional(2000),
    autoRenewal: z.boolean().nullable().optional(),
    noticeDays: z.number().int().min(0).max(3650).nullable().optional(),
    disputeResolution: optional(1000),
    confidentiality: z.enum(LEGAL_CONFIDENTIALITY),
    declarationAccepted: z.boolean().default(false),
    fieldValues: z.record(z.string(), z.unknown()).default({}),
  })
  .refine((v) => v.priority === 'normal' || !!v.priorityReason, {
    path: ['priorityReason'],
    message: 'Explique a urgência',
  })
  .refine((v) => (v.amount == null) === (v.currency == null), {
    path: ['currency'],
    message: 'Informe o valor e a moeda juntos',
  });
export type LegalRequestDraftInput = z.infer<typeof legalRequestDraftInput>;

export const legalCancelInput = z.object({ reason: z.string().trim().min(5).max(2000) });

// ----- Triagem e atendimento (ADR 0024) -----

/** Distribuir: o responsável (ou nenhum). */
export const legalAssignInput = z.object({ assigneeUserId: z.uuid().nullable() });

/** A equipe corrige a origem da minuta indicada por quem pediu (D-14), com o motivo no histórico. */
export const legalDraftOriginInput = z.object({
  draftOrigin: z.enum(LEGAL_DRAFT_ORIGINS),
  reason: z.string().trim().min(5).max(2000),
});

/**
 * Revisão da minuta da outra parte (D-14): por ponto obrigatório da modalidade, atende, foi ajustado
 * ou foi aceito com risco. Aceito com risco exige a nota.
 */
export const legalReviewChecklistInput = z.object({
  items: z
    .array(
      z
        .object({
          point: z.string().trim().min(2).max(60),
          status: z.enum(LEGAL_REVIEW_STATUSES),
          note: z.string().trim().max(2000).nullish(),
        })
        .refine((x) => x.status !== 'accepted_with_risk' || !!x.note, {
          path: ['note'],
          message: 'Explique o risco aceito',
        }),
    )
    .max(40),
});

/** Reclassificar o sigilo, com justificativa no histórico. */
export const legalReclassifyInput = z.object({
  confidentiality: z.enum(LEGAL_CONFIDENTIALITY),
  compartment: z.enum(LEGAL_COMPARTMENTS).nullable(),
  reason: z.string().trim().min(5).max(2000),
});

/** Pedido de complementação e a resposta de quem pediu. */
export const legalMessageInput = z.object({ message: z.string().trim().min(5).max(4000) });

/** Comentário: a equipe escolhe interno ou compartilhado; quem é de fora só compartilha. */
export const legalCommentInput = z.object({
  note: z.string().trim().min(1).max(4000),
  visibility: z.enum(LEGAL_VISIBILITY).default('internal'),
});

/** Providência do atendimento: responsável da equipe (opcional) e prazo. */
export const legalTaskInput = z.object({
  title: z.string().trim().min(1).max(300),
  notes: z
    .string()
    .trim()
    .max(4000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  responsibleUserId: z.uuid().nullable().optional(),
  dueDate: z
    .string()
    .regex(/^(\d{4}-\d{2}-\d{2})?$/)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  visibility: z.enum(LEGAL_VISIBILITY).default('internal'),
});

/** Aprovação jurídica: observação opcional (fica com a equipe). */
export const legalApproveInput = z.object({
  note: z
    .string()
    .trim()
    .max(4000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});

/**
 * Conclusão (ADR 0056): o resultado do atendimento, que quem pediu vê, e o desfecho. Instrumento
 * formalizado exige a composição das assinaturas e o documento assinado (um anexo da solicitação);
 * pareceres e consultas concluem com a análise; instrumento sem assinatura é encerrado com motivo.
 */
export const legalCompleteInput = z.object({
  result: z.string().trim().min(5).max(2000),
  outcome: z.enum(LEGAL_OUTCOMES),
  signedAttachmentId: z.uuid().nullable().optional(),
});

export const legalParticipantInput = z.object({
  userId: z.uuid(),
  reason: z
    .string()
    .trim()
    .max(500)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});

/** Ordem do sigilo: a pessoa pode aumentar o nível padrão da modalidade, nunca reduzir. */
export function legalConfidentialityRank(level: string): number {
  return LEGAL_CONFIDENTIALITY.indexOf(level as (typeof LEGAL_CONFIDENTIALITY)[number]);
}

/** O que falta para enviar (campos base); os específicos são conferidos pelo formulário. */
export function legalSubmitIssues(r: {
  departmentId?: string | null;
  description?: string | null;
  justification?: string | null;
  declarationAccepted?: boolean;
}): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  if (!r.departmentId) issues.push({ path: 'departmentId', message: 'Obrigatório' });
  if (!r.description) issues.push({ path: 'description', message: 'Obrigatório' });
  if (!r.justification) issues.push({ path: 'justification', message: 'Obrigatório' });
  if (!r.declarationAccepted)
    issues.push({ path: 'declarationAccepted', message: 'Confirme a declaração' });
  return issues;
}

/** Perfil financeiro da modalidade (sem a marcação no formulário, vale "optional"). */
export function legalFinancialProfile(schema: {
  financial?: FormFinancialProfile;
}): FormFinancialProfile {
  return schema.financial ?? 'optional';
}

/**
 * Modalidades contratuais (ADR 0029): no envio, centro de custo principal ou rateio válido
 * (100%) e o gestor do contrato. Nas demais, nada é exigido aqui.
 */
export function legalFinancialIssues(
  financial: FormFinancialProfile,
  r: LegalValueFields & { allocation?: CostAllocation | null; businessOwnerId?: string | null },
): { path: string; message: string }[] {
  if (financial === 'none') return [];
  const issues = legalValueIssues(financial, r, true);
  if (financial !== 'contract') return issues;
  issues.push(...costAllocationIssues(r.allocation, true));
  if (!r.businessOwnerId) issues.push({ path: 'businessOwnerId', message: 'Obrigatório' });
  return issues;
}

// ----- Estrutura do valor contratual (ADR 0030) -----

export interface LegalValueFields {
  amount?: string | null;
  currency?: string | null;
  periodicity?: string | null;
  valueStatus?: string | null;
  valueNature?: string | null;
  valueBasis?: string | null;
}

/**
 * Coerência do valor (no rascunho e no envio) e obrigatórios (só no envio). Não presume valor
 * fixo nem conhecido: "a definir" e "sem valor" não levam montante. Combinações incoerentes
 * (valor por período com pagamento único, entrega com periodicidade de calendário...) são
 * recusadas.
 */
export function legalValueIssues(
  financial: FormFinancialProfile,
  r: LegalValueFields,
  submit: boolean,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  if (financial === 'none') return issues;
  const add = (path: string, message: string) => issues.push({ path, message });
  const status = r.valueStatus ?? null;
  if (!status) {
    if (submit && financial === 'contract') add('valueStatus', 'Informe se há valor financeiro');
    if (r.amount) add('valueStatus', 'Informe se o valor está definido');
    return issues;
  }
  if (status !== 'defined') {
    if (r.amount) add('amount', 'Valor só quando estiver definido');
    if (status === 'none' && (r.valueNature || r.valueBasis)) {
      add('valueNature', 'Sem valor financeiro: deixe natureza e base em branco');
    }
  } else if (submit) {
    if (!r.amount) add('amount', 'Informe o valor');
    if (!r.valueNature) add('valueNature', 'Obrigatório');
    if (!r.valueBasis) add('valueBasis', 'Obrigatório');
    if (!r.periodicity) add('periodicity', 'Obrigatório');
  }
  if (r.amount && !r.currency) add('currency', 'Informe a moeda');
  const p = r.periodicity ?? null;
  const b = r.valueBasis ?? null;
  if (b === 'per_period' && p && !RECURRING.includes(p)) {
    add('periodicity', 'Valor por período precisa de uma periodicidade (mensal, anual...)');
  }
  if (b === 'per_delivery' && p && p !== 'per_delivery') {
    add('periodicity', 'Valor por entrega usa a periodicidade "por entrega"');
  }
  if (p === 'per_delivery' && b && b !== 'per_delivery') {
    add('valueBasis', 'Periodicidade por entrega pede a base "valor por entrega"');
  }
  if (r.valueNature === 'per_delivery' && b && b !== 'per_delivery') {
    add('valueBasis', 'Natureza por entrega pede a base "valor por entrega"');
  }
  return issues;
}
